import { useState } from 'react';
import type { CreateFeedbackBody, FeedbackType, FeedbackUrgency } from '@malash-office/types';
import { validateFiles } from '@/components/common/media/mediaValidation';

/** What the form opens with: the type picked on the way in, and what was collected automatically. */
export interface NewFeedbackSeed {
  type: FeedbackType;
  link: string;
  module: string;
  /** Null when the capture failed — the checkbox is then off and disabled. */
  screenshot: Blob | null;
}

/** "פנייה חדשה" — the fields, the files, and the one error the mockup shows. */
export const useNewFeedbackForm = (seed: NewFeedbackSeed) => {
  const [type, setType] = useState<FeedbackType>(seed.type);
  const [urgency, setUrgency] = useState<FeedbackUrgency>('medium');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [link, setLink] = useState(seed.link);
  const [withScreenshot, setWithScreenshot] = useState(!!seed.screenshot);
  const [files, setFiles] = useState<File[]>([]);
  const [missing, setMissing] = useState(false);
  const [fileErrors, setFileErrors] = useState<string[]>([]);

  const addFiles = (incoming: File[]) => {
    const { accepted, errors } = validateFiles(incoming, 'feedbackFile');
    setFileErrors(errors);
    if (accepted.length) setFiles(current => [...current, ...accepted]);
  };

  /** The body, or null with "יש למלא נושא ותיאור" shown. */
  const toBody = (): CreateFeedbackBody | null => {
    if (!title.trim() || !description.trim()) {
      setMissing(true);
      return null;
    }
    return {
      type,
      urgency,
      title: title.trim(),
      description: description.trim(),
      link: link.trim() || null,
      module: seed.module
    };
  };

  return {
    type,
    setType,
    urgency,
    setUrgency,
    title,
    setTitle: (value: string) => {
      setMissing(false);
      setTitle(value);
    },
    description,
    setDescription: (value: string) => {
      setMissing(false);
      setDescription(value);
    },
    link,
    setLink,
    withScreenshot,
    toggleScreenshot: () => setWithScreenshot(current => !current && !!seed.screenshot),
    files,
    addFiles,
    removeFile: (index: number) => setFiles(current => current.filter((_file, i) => i !== index)),
    fileErrors,
    missing,
    toBody,
    screenshot: withScreenshot ? seed.screenshot : null
  };
};

export type NewFeedbackForm = ReturnType<typeof useNewFeedbackForm>;
